
import { PEOProvider } from '@/types/peo.types';

/**
 * Normalize a string for comparison by removing common suffixes and whitespace
 */
export const normalizeProviderName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/,?\s*(inc|llc|corporation|corp|company|co|group|peo|services|service|solutions|llp)\.?$/i, '');
};

/**
 * Find potential duplicate PEO providers based on similar names
 */
export const findPotentialDuplicates = (providers: PEOProvider[]): { 
  group: string; 
  items: PEOProvider[];
}[] => {
  const normalizedMap = new Map<string, PEOProvider[]>();
  
  // Group providers by normalized names
  providers.forEach(provider => {
    const normalizedName = normalizeProviderName(provider.name);
    if (!normalizedMap.has(normalizedName)) {
      normalizedMap.set(normalizedName, []);
    }
    normalizedMap.get(normalizedName)?.push(provider);
  });
  
  // Filter to only include groups with multiple providers
  const duplicateGroups = Array.from(normalizedMap.entries())
    .filter(([_, group]) => group.length > 1)
    .map(([name, items]) => ({ group: name, items }));
  
  return duplicateGroups;
};

/**
 * Returns a suggested provider to keep from a group of duplicates
 * Prefers shorter names without legal suffixes
 */
export const suggestProviderToKeep = (providers: PEOProvider[]): PEOProvider => {
  // Sort providers by name length and lack of legal suffixes
  return [...providers].sort((a, b) => {
    // Prefer names without legal suffixes
    const aHasSuffix = /\b(inc|llc|corporation|corp)\b/i.test(a.name);
    const bHasSuffix = /\b(inc|llc|corporation|corp)\b/i.test(b.name);
    
    if (aHasSuffix !== bHasSuffix) {
      return aHasSuffix ? 1 : -1;
    }
    
    // Then prefer shorter names
    return a.name.length - b.name.length;
  })[0];
};

/**
 * Extracts provider IDs from a group of duplicates
 */
export const getProviderIdsToDelete = (
  providers: PEOProvider[], 
  providerToKeepId: string
): string[] => {
  return providers
    .filter(provider => provider.id !== providerToKeepId)
    .map(provider => provider.id);
};
